import React, { useEffect, useState } from 'react';
import {
  Button,
  Box,
  Modal,
  TextField,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Typography,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  Store as StoreIcon
} from '@mui/icons-material';
import { addUser, deleteUser, fetchUsers, updateUser } from '../api/superAdmin';
import UserDetailsModal from '../components/UserDetailsModal';

// Define a User type
interface User {
  id: number;
  email: string;
  password?: string;
  role: 'super_admin' | 'location_admin' | 'customer';
  name: string;
  phone?: string;
  location_name?: string | null;
  location_id?: number | null;
}

// Main Users Page Component
const UsersPage: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [openModal, setOpenModal] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<number | null>(null);
  const [form, setForm] = useState<Partial<User>>({
    email: '',
    password: '',
    role: 'customer',
    name: '',
    phone: '',
    location_id: null,
  });
  const [selectedUser, setSelectedUser] = useState<number | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const loadUsers = async () => {
    try {
      const data = await fetchUsers();
      setUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching users:', err);
      setUsers([]);
    }
  };

  const handleSubmit = async () => {
    try {
      const userForm = {
        email: form.email || '',
        password: form.password || '',
        role: form.role || 'customer',
        name: form.name || '',
        phone: form.phone || '',
        location_id: form.location_id || null,
      };

      if (form.id) {
        await updateUser(form.id, userForm);
      } else {
        await addUser(userForm);
      }

      setOpenModal(false);
      setForm({
        email: '',
        password: '',
        role: 'customer',
        name: '',
        phone: '',
        location_id: null,
      });
      loadUsers();
    } catch (err) {
      console.error('Error submitting user:', err);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteUser(id);
      loadUsers();
      setDeleteDialogOpen(false);
    } catch (err) {
      console.error('Error deleting user:', err);
    }
  };

  const openDeleteDialog = (id: number) => {
    setUserToDelete(id);
    setDeleteDialogOpen(true);
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'super_admin': return <AdminIcon color="error" />;
      case 'location_admin': return <StoreIcon color="primary" />;
      default: return <PersonIcon color="action" />;
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'super_admin': return 'error';
      case 'location_admin': return 'primary';
      default: return 'default';
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  return (
    <Box sx={{ p: isMobile ? 1 : 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" fontWeight="bold" color="primary">
          User Management
        </Typography>
        <Button 
          variant="contained" 
          startIcon={<AddIcon />} 
          onClick={() => setOpenModal(true)}
          sx={{ borderRadius: 2 }}
        >
          Add User
        </Button>
      </Box>

      {isMobile ? (
        // Mobile Card View
        <Grid container spacing={2}>
          {users.map((user) => (
            <Grid item xs={12} key={user.id}>
              <Card sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {getRoleIcon(user.role)}
                      <Typography variant="h6" sx={{ ml: 1 }}>
                        {user.name}
                      </Typography>
                    </Box>
                    <Chip 
                      label={user.role.replace('_', ' ')} 
                      size="small" 
                      color={getRoleColor(user.role)}
                      variant="outlined"
                    />
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {user.email}
                  </Typography>
                  
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    {user.location_name || 'No location assigned'}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <IconButton 
                      size="small" 
                      color="primary"
                      onClick={() => {
                        setSelectedUser(user.id);
                        setDetailsOpen(true);
                      }}
                    >
                      <PersonIcon />
                    </IconButton>
                    <Box>
                      <IconButton 
                        size="small" 
                        color="primary"
                        onClick={() => {
                          setForm({ ...user, password: '' });
                          setOpenModal(true);
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        color="error"
                        onClick={() => openDeleteDialog(user.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        // Desktop Table View
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow
                key={user.id}
                hover
                onClick={() => {
                  setSelectedUser(user.id);
                  setDetailsOpen(true);
                }}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {getRoleIcon(user.role)}
                    <Typography sx={{ ml: 1 }}>{user.name}</Typography>
                  </Box>
                </TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>
                  <Chip 
                    label={user.role.replace('_', ' ')} 
                    size="small" 
                    color={getRoleColor(user.role)}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>{user.location_name || 'N/A'}</TableCell>
                <TableCell>
                  <IconButton 
                    size="small" 
                    color="primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      setForm({ ...user, password: '' });
                      setOpenModal(true);
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton 
                    size="small" 
                    color="error"
                    onClick={(e) => {
                      e.stopPropagation();
                      openDeleteDialog(user.id);
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <UserDetailsModal
        userId={selectedUser}
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
      />

      {/* Add/Edit User Modal */}
      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: isMobile ? '90%' : 400,
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" gutterBottom>
            {form.id ? 'Edit User' : 'Add User'}
          </Typography>
          
          <TextField
            label="Name"
            value={form.name || ''}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Email"
            value={form.email || ''}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Password"
            type="password"
            value={form.password || ''}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Phone"
            value={form.phone || ''}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            fullWidth
            margin="normal"
          />
          <Select
            value={form.role || 'customer'}
            onChange={(e) => setForm({ ...form, role: e.target.value as User['role'] })}
            fullWidth
            margin="dense"
          >
            <MenuItem value="super_admin">Super Admin</MenuItem>
            <MenuItem value="location_admin">Location Admin</MenuItem>
            <MenuItem value="customer">Customer</MenuItem>
          </Select>
          <Button 
            variant="contained" 
            fullWidth 
            onClick={handleSubmit} 
            sx={{ mt: 2 }}
          >
            Save
          </Button>
        </Box>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this user?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button 
            color="error" 
            onClick={() => userToDelete && handleDelete(userToDelete)}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UsersPage;